import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { queries } from '~/keys';

// What the sales-offer wizard sends for every step: preview, project offer and unit offer.
export type SalesOfferCopy = { headline: string; intro: string; highlights: string[] };

export type SalesOfferInput = {
  propertyId: string;
  unitId?: string | null;
  clientName?: string;
  personalMessage?: string;
  showDeveloper: boolean;
  // The copy shown in the preview, so the stored offer matches it.
  copy?: SalesOfferCopy | null;
};

type GenerateSalesOfferResponse = {
  success: true;
  data: {
    dealId?: string;
    offerUrl: string;
    priceHoldExpiresAt?: string;
    reused?: boolean;
  };
};

const offerBody = (input: SalesOfferInput) => ({
  unitId: input.unitId || undefined,
  clientName: input.clientName || undefined,
  personalMessage: input.personalMessage || undefined,
  showDeveloper: input.showDeveloper,
  copy: input.copy ?? undefined,
});

// The offer exactly as it will be stored, with no side effects (no file, no price hold).
export const usePreviewSalesOffer = () => {
  const authStore = useAuthStore();
  return useMutation({
    mutationFn: (input: SalesOfferInput) =>
      $fetch<{ success: true; data: { html: string; copy: SalesOfferCopy } }>(
        `/api/properties/${input.propertyId}/sales-offer/preview`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${authStore.token}` },
          body: offerBody(input),
        },
      ).then((r) => r.data),
    onError: (error) => useErrorHandler().catchErrors(error),
  });
};

// Stores the offer: a unit offer also creates the 24h price hold; a project overview holds nothing.
export const useGenerateSalesOffer = () => {
  const authStore = useAuthStore();
  return useMutation({
    mutationFn: (input: SalesOfferInput) => {
      const url = input.unitId
        ? `/api/properties/${input.propertyId}/units/${input.unitId}/sales-offer`
        : `/api/properties/${input.propertyId}/sales-offer`;
      return $fetch<GenerateSalesOfferResponse>(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authStore.token}` },
        body: offerBody(input),
      });
    },
    onError: (error) => useErrorHandler().catchErrors(error),
  });
};

// "Remember my presentation settings": the developer-branding choice only.
export const useSaveOfferSettings = () => {
  const authStore = useAuthStore();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (showDeveloper: boolean) =>
      $fetch('/api/marketing/brand-kit/offer-settings', {
        method: 'PUT',
        headers: { Authorization: `Bearer ${authStore.token}` },
        body: { showDeveloper },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queries.marketing.brandKit.queryKey }),
    onError: (error) => useErrorHandler().catchErrors(error),
  });
};

export default useGenerateSalesOffer;
